/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { type Polling, CoreEventServiceKeyE, EventService, HttpService, I18nService, PermissionService, PollingService, ToastService } from '@dm/core';
import { type QualityInspect } from 'app/common/types/mo';
import { type OEM } from 'app/common/types/oem';
import { FeatureConfig } from 'app/config/feature';
import { SWITCH } from 'app/config/switch';

@Component({
    selector: 'app-quality-inspect',
    templateUrl: './quality-inspect.component.html',
    styleUrls: ['./quality-inspect.component.scss'],
})
export class QualityInspectComponent implements OnInit, OnDestroy {
    isLoginPage = true;
    isInitializePage = true;
    showQualityInspect = false;
    qualityInspectInfo = this.i18nService.get('home.deploymentQualityInspection_disable_info');
    private polling$: Polling;
    oem: OEM = (window as any).oem;
    isNotOem: boolean;
    isFirstRequest = true;

    constructor(
        private i18nService: I18nService,
        private permissionService: PermissionService,
        private toastService: ToastService,
        private pollingService: PollingService,
        private httpService: HttpService,
        private router: Router,
        private eventService: EventService,
    ) { }

    /**
     * 检测header上方提示信息的变更，可能导致高度变化，需适配部分场景的显示优化
     * 场景包括：侧滑框（极简模式自定义侧滑框）、气泡toast、高级搜索全局模式、登陆信息等场景的top值
     * 窗口尺寸变化可能导致提示信息自动换行，高度发生变化，也需要重新适配
     */
    @HostListener('window:resize')
    adjustChangeOfTipsOverHeader(): void {
        // 异步执行，确保元素页面的呈现已经变化
        setTimeout(() => {
            this.eventService.emit(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER);
        });
    }

    ngOnInit(): void {
        this.isNotOem = this.oem.vendor === 'huawei';
        this.watchRouterChange();
    }

    watchRouterChange(): void {
        this.router.events.subscribe((evt: any) => {
            if (evt instanceof NavigationEnd) {
                this.isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/sso';
                this.isInitializePage = window.location.pathname === '/initialize';
            }

            if (this.isQualityInspectNeeded()) {
                this.getQualityInspect();
            }
        });
    }

    getQualityInspect(): void {
        // 轮询质检信息
        const POLLING_INTERVAL = 180;
        if (!this.polling$) {
            this.polling$ = this.pollingService.add(this.httpService.makeUrl('qualityInspect.get', 'V2'), POLLING_INTERVAL).immediate();
            this.polling$.subscribe(({ data }: { data: QualityInspect }) => {
                const oldValue = this.showQualityInspect;
                this.showQualityInspect = data.ISNOTIFICATIONNEEDED;
                this.isFirstRequest = false;

                if (oldValue !== this.showQualityInspect) {
                    this.adjustChangeOfTipsOverHeader();
                }
            });
            this.polling$.err.subscribe(() => {
                const oldValue = this.showQualityInspect;
                this.showQualityInspect = false;

                // 首次请求失败才报错，轮询不报错
                if (this.isFirstRequest) {
                    this.toastService.error(this.i18nService.get('home.deploymentQualityInspection_err_info'));
                    this.isFirstRequest = false;
                }

                if (oldValue !== this.showQualityInspect) {
                    this.adjustChangeOfTipsOverHeader();
                }
            });
        }
    }

    /**
     * 非登录页，非初始化页面，有quality_inspect读权限，非租户，非白牌,非计算型存储设备才轮询质检信息
     */
    isQualityInspectNeeded(): boolean {
        return !this.isLoginPage &&
            !this.isInitializePage &&
            this.permissionService.hasPermission('quality_inspect_R') &&
            !SWITCH.IS_VSTORE_SYSTEM &&
            FeatureConfig.IsQualityInspectNeeded &&
            this.isNotOem;
    }

    ngOnDestroy(): void {
        this.pollingService.remove(this.polling$);
    }
}
